import { useCallback, useMemo, type ReactNode } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"

export interface LowerPanelTab {
  id: string
  label: string
  icon?: ReactNode
  badgeSlot?: ReactNode
}

export interface LowerPanelTouchHandlers {
  onTouchStart?: React.TouchEventHandler<HTMLDivElement>
  onTouchMove?: React.TouchEventHandler<HTMLDivElement>
  onTouchEnd?: React.TouchEventHandler<HTMLDivElement>
  onClickCapture?: React.MouseEventHandler<HTMLDivElement>
}

export interface LowerPanelProps {
  tabs: LowerPanelTab[]
  activeTabId: string
  onTabChange: (id: string) => void
  collapsed?: boolean
  expanded?: boolean
  onExpandUp?: () => void
  onCollapseDown?: () => void
  isMobile?: boolean
  tabBarHandlers?: LowerPanelTouchHandlers
  children?: ReactNode
  className?: string
}

export function LowerPanel({
  tabs,
  activeTabId,
  onTabChange,
  collapsed = false,
  expanded = false,
  onExpandUp,
  onCollapseDown,
  isMobile = false,
  tabBarHandlers,
  children,
  className,
}: LowerPanelProps) {
  const handleTab = useCallback(
    (id: string) => {
      onTabChange(id)
    },
    [onTabChange],
  )

  const tabBarProps = useMemo(() => {
    if (!isMobile || !tabBarHandlers) return {}
    return tabBarHandlers
  }, [isMobile, tabBarHandlers])

  return (
    <div
      className={cn(
        "flex flex-col h-full min-h-0 overflow-hidden",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center border-b border-border shrink-0 h-11 bg-background",
          isMobile && "touch-none select-none",
        )}
        {...tabBarProps}
      >
        <div
          className={cn(
            "flex-1 flex items-center gap-0.5 min-w-0",
            isMobile
              ? "px-4 overflow-hidden"
              : "px-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
        >
          {tabs.map((tab) => {
            const active = activeTabId === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTab(tab.id)}
                className={cn(
                  "shrink-0 flex items-center gap-1.5 py-1 rounded-md text-caption transition-colors cursor-pointer",
                  isMobile ? "px-2" : "px-2.5",
                  active
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                )}
              >
                {tab.icon}
                <span>{tab.label}</span>
                {tab.badgeSlot && !isMobile && tab.badgeSlot}
              </button>
            )
          })}
        </div>

        <div className={cn("shrink-0 flex items-center", isMobile ? "mr-4" : "mr-4 gap-0.5")}>
          {isMobile ? (
            collapsed ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onExpandUp}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                aria-label="Restore lower panel"
              >
                <ChevronUp size={16} strokeWidth={2} />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCollapseDown}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                aria-label={expanded ? "Restore lower panel" : "Collapse lower panel"}
              >
                <ChevronDown size={16} strokeWidth={2} />
              </Button>
            )
          ) : (
            <>
              {!expanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onExpandUp}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                  aria-label={collapsed ? "Restore lower panel" : "Expand lower panel"}
                >
                  <ChevronUp size={12} strokeWidth={2} />
                </Button>
              )}
              {!collapsed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCollapseDown}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                  aria-label={expanded ? "Restore lower panel" : "Collapse lower panel"}
                >
                  <ChevronDown size={12} strokeWidth={2} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="flex-1 min-h-0 overflow-hidden bg-card">{children}</div>
      )}
    </div>
  )
}
